import { useEffect, useMemo, useRef, useState } from "react";

import { measureTextWidth } from "../helpers/measureTextWidth";

// Distribui a largura de uma linha de itens que truncam com reticências
// (os badges de fila/conexão/atendente do card de ticket).
//
// Por que não deixar só o flexbox resolver: flex-shrink encolhe todo mundo na
// mesma proporção e não entende "este aqui só cede depois que os outros já
// cederam". Com min-width em cada item o flexbox até respeita os pisos, mas
// quando a soma dos pisos não cabe (cards estreitos, mobile) ele estoura a
// linha e o item mais à direita — o atendente — é o que some no overflow.
//
// Aqui a conta é feita na mão, em px, na ordem de prioridade declarada:
//   1. ninguém encolhe se tudo couber;
//   2. faltando espaço, cedem primeiro os itens de menor `priority`, cada um
//      até o seu piso (`minChars` caracteres + reticências);
//   3. só se ainda faltar espaço os pisos são violados, e aí proporcionalmente.
//
// items: [{ key, text, chrome, minChars, letterSpacing, maxWidth, priority }]
//   chrome    = largura fixa do item além do texto (ícone, gap, padding)
//   minChars  = piso em caracteres; texto menor que isso nunca é truncado
//   priority  = maior valor cede espaço por último
//
// Retorna { ref, widths }: `ref` vai no container da linha e `widths` é um mapa
// key -> largura em px (ou undefined enquanto não há medição / quando tudo cabe,
// caso em que o layout natural do flexbox já está correto).
// Marque com este atributo o <span> que carrega o texto de um item: é dele que
// sai a fonte usada na medição (o container tem outro tamanho/peso).
export const BADGE_TEXT_ATTR = "data-badge-text";

export const useBadgeRowWidths = (items, { gap = 0, enabled = true } = {}) => {
  const ref = useRef(null);
  const [available, setAvailable] = useState(0);
  const [font, setFont] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof ResizeObserver === "undefined") return undefined;

    // a fonte real (família, tamanho, peso) sai do próprio DOM: assim a medição
    // não depende de repetir aqui o que está no CSS nem do fallback de fonte
    const sample = el.querySelector(`[${BADGE_TEXT_ATTR}]`) || el;
    const computed = window.getComputedStyle(sample);
    setFont({
      fontSize: parseFloat(computed.fontSize) || 14,
      fontWeight: computed.fontWeight || 400,
      fontFamily: computed.fontFamily,
    });

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width || 0;
      // ignora ruído sub-pixel para não re-renderizar o card à toa
      setAvailable((prev) => (Math.abs(prev - width) < 1 ? prev : width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  const widths = useMemo(() => {
    const result = {};
    if (!enabled || !font || !available || !items.length) return result;

    const measure = (text, letterSpacing) =>
      measureTextWidth(text, { ...font, letterSpacing });

    const boxes = items.map((item) => {
      // os badges são uppercase por CSS; medir em minúsculo subestimaria
      const text = String(item.text || "").toUpperCase();
      const letterSpacing = item.letterSpacing || 0;
      const floorText =
        text.length > item.minChars
          ? `${text.slice(0, item.minChars)}…`
          : text;

      // +1px de folga: sem ela o arredondamento final deixa o texto 0,x px mais
      // largo que a caixa e o browser troca o último caractere por reticências
      const natural = Math.min(
        item.chrome + measure(text, letterSpacing) + 1,
        item.maxWidth || Infinity
      );
      const floor = Math.min(
        item.chrome + measure(floorText, letterSpacing) + 1,
        natural
      );

      return {
        key: item.key,
        priority: item.priority || 0,
        natural,
        floor,
        // último recurso: sobra só o ícone e o padding, o texto some
        hardFloor: Math.min(item.chrome, natural),
      };
    });

    const totalNatural = boxes.reduce((sum, b) => sum + b.natural, 0);
    const totalGap = gap * (boxes.length - 1);
    let deficit = totalNatural + totalGap - available;
    // cabe tudo: deixa o flexbox posicionar naturalmente
    if (deficit <= 0) return result;

    const current = new Map(boxes.map((b) => [b.key, b.natural]));
    const tiers = [...new Set(boxes.map((b) => b.priority))].sort((a, b) => a - b);

    // Corta por faixa de prioridade: a faixa mais baixa cede tudo o que pode
    // antes de a faixa seguinte perder um pixel. Dentro da faixa o corte é
    // proporcional à folga de cada um, para dois badges longos encolherem
    // juntos em vez de um sozinho.
    const concede = (limitOf) => {
      for (const tier of tiers) {
        if (deficit <= 0) return;
        const tierBoxes = boxes.filter((b) => b.priority === tier);
        const slack = tierBoxes.reduce(
          (sum, b) => sum + Math.max(0, current.get(b.key) - limitOf(b)),
          0
        );
        if (slack <= 0) continue;

        const taken = Math.min(deficit, slack);
        tierBoxes.forEach((b) => {
          const room = Math.max(0, current.get(b.key) - limitOf(b));
          current.set(b.key, current.get(b.key) - (room / slack) * taken);
        });
        deficit -= taken;
      }
    };

    concede((b) => b.floor);
    // Nem todos os pisos cabem (card estreito, mobile): em vez de encolher todo
    // mundo junto, a mesma ordem de prioridade vale de novo — fila e conexão
    // cedem até sumir o texto para o atendente manter os caracteres dele.
    concede((b) => b.hardFloor);

    // nem os hardFloors cabem: aí não há como priorizar, todos cedem
    // proporcionalmente para nada estourar a linha
    if (deficit > 0) {
      const total = boxes.reduce((sum, b) => sum + current.get(b.key), 0);
      if (total > 0) {
        boxes.forEach((b) => {
          const width = current.get(b.key);
          current.set(b.key, width - (width / total) * deficit);
        });
      }
    }

    boxes.forEach((b) => {
      result[b.key] = Math.max(0, Math.floor(current.get(b.key)));
    });
    return result;
  }, [items, gap, available, font, enabled]);

  return { ref, widths };
};

export default useBadgeRowWidths;
